import { Group } from '../../domain/object/group';
import { Product } from '../../domain/object/product';
import { Project } from '../../domain/object/project';

export type QantProductKey = Product.Key;

export type QantProductPermission = {
  isAvailable: boolean;
  isAdmin: boolean;
  migration?: {
    useKarteAsMain: boolean;
  };
  speakMigration?: {
    useKarteAsMain: boolean;
  };
};

/**
 * メニュー出し分け用の事実 (`@rt/global-navi/contract` の `QantNaviFacts` と構造互換)。
 * 評価器 (`@rt/global-navi`) が auth ドメインの生構造 (role / Group.Policy) を知らずに済むよう、
 * boolean に畳んだ事実のみを持つ。
 */
export type QantNaviFacts = {
  /** 社内アカウントか (RightTouch 社員 or Employee grant 保持者)。`employee` condition が参照する。 */
  isEmployee: boolean;
  /**
   * product ごとの管理権限 (v1 `HasProduct管理権限` 相当)。
   * `qantProductAdmin` condition が参照する。
   */
  productAdmin: Partial<Record<QantProductKey, boolean>>;
  /**
   * product ごとの PII アクセス権限 (v1 `HasPiiAccessNow(product)` 相当)。
   * `qantProductPii` condition が参照する。
   */
  productPii: Partial<Record<QantProductKey, boolean>>;
  /** Project `featureFlags.qantSmsEnabled === true` のときのみ true。 */
  qantSmsEnabled?: boolean;
};

export type QantNaviInput = {
  user: {
    id: string;
    name: string;
    email: string;
    avatarUrl?: string;
    isSystemAdmin: boolean;
    roleLabel?: string;
    grants?: {
      isSystemAdminEligible: boolean;
      hasActiveSystemAdmin: boolean;
      isPiiEligible: boolean;
      hasActivePiiAccess: boolean;
      isEmployee: boolean;
    };
  };
  organization: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
  project: {
    id: string;
    name: string;
    enabledProducts: QantProductKey[];
    productPermissions: Partial<Record<QantProductKey, QantProductPermission>>;
  };
  /** メニュー出し分け用の事実。`@rt/global-navi` の QANT 系 condition 評価に使われる。 */
  facts?: QantNaviFacts;
  organizations?: Array<{
    id: string;
    name: string;
    avatarUrl?: string;
    hasOrgAdminAccess?: boolean;
    projects: Array<{
      id: string;
      name: string;
      karteProjectId?: string;
      hasProjectSettingsAccess?: boolean;
    }>;
  }>;
};

/** V2: account / current? 分離。`@rt/global-navi/contract` の `QantNaviInputV2` と構造互換。 */
export type QantNaviInputV2 = {
  account: {
    user: QantNaviInput['user'];
    organizations: Array<{
      id: string;
      name: string;
      avatarUrl?: string;
      hasOrgAdminAccess?: boolean;
      karteOrganizationId?: string;
      projects: Array<{
        id: string;
        name: string;
        karteProjectId?: string;
        hasProjectSettingsAccess?: boolean;
      }>;
    }>;
  };
  current?: {
    organization: QantNaviInput['organization'];
    project: QantNaviInput['project'];
    facts?: QantNaviFacts;
  };
};

export type ConvertToQantNaviInputV2Context = {
  currentOrganizationId?: string;
  currentProjectId?: string;
};

export type ConvertToQantNaviInputV2Options = {
  karteProjectMappings?: ReadonlyMap<string, string>;
  /** rtOrganizationId → karteOrganizationId */
  karteOrganizationMappings?: ReadonlyMap<string, string>;
};

/**
 * `convertToQantNaviInputV2` が要求する最小入力。auth の public / internal / interservice AuthInfo がこの構造を満たせる。
 */
export type QantNaviSource = {
  Account: {
    id: string;
    name: string;
    email: string;
    profileImageSrc: string;
    isEmployee: boolean;
    hasActiveSystemAdmin: boolean;
    hasActivePiiAccess: boolean;
    isSystemAdminEligible: boolean;
    isPiiEligible: boolean;
  };
  Organization: ReadonlyArray<{
    id: string;
    name: string;
    profileImageSrc: string;
    role?: { key: string; name: string };
    projects: ReadonlyArray<{
      id: string;
      name: string;
      productSettings: Project.ProductSettings;
      featureFlags?: Project.FeatureFlags;
      group?: { policy: Group.Policy; isProjectAdmin: boolean } | null;
    }>;
  }>;
  /**
   * 所属 `Organization[]` に載らない表示中プロジェクト。
   * `system_admin_product_entry` のとき V2 `current` の入力になる。
   */
  RequestedProjectContext?: {
    organization: {
      id: string;
      name: string;
      profileImageSrc: string;
    };
    project: {
      id: string;
      name: string;
      productSettings: Project.ProductSettings;
      featureFlags?: Project.FeatureFlags;
      access: { kind: 'membership' } | { kind: 'system_admin_product_entry' };
      membership?: {
        group?: { policy: Group.Policy; isProjectAdmin: boolean } | null;
      } | null;
    };
  };
};

/**
 * QANT AuthInfo から V2 transport（account / current?）を抽出する。
 * current が解決できない場合も account は返す（一覧消失バグの本丸）。
 * 所属に無くても `RequestedProjectContext.access.kind === 'system_admin_product_entry'`
 * かつ active system admin なら current を載せる。
 */
export const convertToQantNaviInputV2 = (
  authInfo: QantNaviSource,
  ctx: ConvertToQantNaviInputV2Context = {},
  options?: ConvertToQantNaviInputV2Options,
): QantNaviInputV2 => {
  const account = authInfo.Account;
  const karteProjectMappings = options?.karteProjectMappings;
  const karteOrganizationMappings = options?.karteOrganizationMappings;

  const organizations = authInfo.Organization.map((org) => {
    const karteOrganizationId = karteOrganizationMappings?.get(org.id);
    return {
      id: org.id,
      name: org.name,
      ...(org.profileImageSrc ? { avatarUrl: org.profileImageSrc } : {}),
      hasOrgAdminAccess: hasOrganizationSettingsAccessForNavi(account, org),
      ...(karteOrganizationId ? { karteOrganizationId } : {}),
      projects: org.projects.map((p) => {
        const karteProjectId = karteProjectMappings?.get(p.id);
        return {
          id: p.id,
          name: p.name,
          ...(karteProjectId ? { karteProjectId } : {}),
          hasProjectSettingsAccess: hasProjectSettingsAccessForNavi(account, org, p),
        };
      }),
    };
  });

  const grants = {
    isSystemAdminEligible: account.isSystemAdminEligible,
    hasActiveSystemAdmin: account.hasActiveSystemAdmin,
    isPiiEligible: account.isPiiEligible,
    hasActivePiiAccess: account.hasActivePiiAccess,
    isEmployee: account.isEmployee,
  };

  const result: QantNaviInputV2 = {
    account: {
      user: {
        id: account.id,
        name: account.name,
        email: account.email,
        ...(account.profileImageSrc ? { avatarUrl: account.profileImageSrc } : {}),
        isSystemAdmin: account.hasActiveSystemAdmin,
        grants,
      },
      organizations,
    },
  };

  if (!ctx.currentOrganizationId || !ctx.currentProjectId) {
    return result;
  }

  const organization = authInfo.Organization.find((o) => o.id === ctx.currentOrganizationId);
  const project = organization?.projects.find((p) => p.id === ctx.currentProjectId);
  if (organization && project) {
    assignCurrent({
      result,
      account,
      organization,
      project,
      productAvailability: 'group_policy',
      roleLabel: toNaviRoleLabel({ group: project.group }),
    });
    return result;
  }

  const requested = authInfo.RequestedProjectContext;
  if (
    requested &&
    requested.organization.id === ctx.currentOrganizationId &&
    requested.project.id === ctx.currentProjectId &&
    requested.project.access.kind === 'system_admin_product_entry' &&
    account.hasActiveSystemAdmin
  ) {
    assignCurrent({
      result,
      account,
      organization: requested.organization,
      project: {
        id: requested.project.id,
        name: requested.project.name,
        productSettings: requested.project.productSettings,
        featureFlags: requested.project.featureFlags,
        group: requested.project.membership?.group ?? null,
      },
      productAvailability: 'plan_enabled',
      roleLabel: 'システム管理者',
    });
  }

  return result;
};

const assignCurrent = (params: {
  result: QantNaviInputV2;
  account: QantNaviSource['Account'];
  organization: { id: string; name: string; profileImageSrc: string };
  project: {
    id: string;
    name: string;
    productSettings: Project.ProductSettings;
    featureFlags?: Project.FeatureFlags;
    group?: { policy: Group.Policy; isProjectAdmin: boolean } | null;
  };
  productAvailability: 'group_policy' | 'plan_enabled';
  roleLabel: string;
}): void => {
  const { result, account, organization, project, productAvailability, roleLabel } = params;
  result.account.user.roleLabel = roleLabel;

  const productPermissions: Partial<Record<QantProductKey, QantProductPermission>> = {};
  const enabledProducts: QantProductKey[] = [];

  for (const product of Product.List) {
    const productKey = product.key as QantProductKey;
    const isAvailable =
      productAvailability === 'plan_enabled'
        ? Project.isProductEnabled({ productSettings: project.productSettings, productKey: product.key })
        : Project.isProductAccessible({
            productSettings: project.productSettings,
            productKey: product.key,
            group: project.group,
            account,
          });
    productPermissions[productKey] = {
      isAvailable,
      isAdmin: isProductAdmin({ account, project, productKey: product.key }),
      ...(productKey === 'voc'
        ? {
            migration: getVocMigration({ project }),
          }
        : {}),
      ...(productKey === 'speak'
        ? {
            speakMigration: {
              useKarteAsMain: Group.Policy.UseKarteAsMain({ policy: project.group?.policy ?? {}, productKey: 'speak' }),
            },
          }
        : {}),
    };
    if (isAvailable) enabledProducts.push(productKey);
  }

  result.current = {
    organization: {
      id: organization.id,
      name: organization.name,
      ...(organization.profileImageSrc ? { avatarUrl: organization.profileImageSrc } : {}),
    },
    project: {
      id: project.id,
      name: project.name,
      enabledProducts,
      productPermissions,
    },
    facts: buildQantNaviFacts({ account, project }),
  };
};

type NaviOrganizationSource = Pick<QantNaviSource['Organization'][number], 'role'>;
type NaviProjectSource = Pick<QantNaviSource['Organization'][number]['projects'][number], 'group'>;
type NaviAccountSource = Pick<QantNaviSource['Account'], 'hasActiveSystemAdmin'>;

/**
 * 組織設定リンク (歯車) の出し分け。`canShowOrganizationSettingsLink` と同じ判定。
 */
export const hasOrganizationSettingsAccessForNavi = (account: NaviAccountSource, org: NaviOrganizationSource): boolean =>
  account.hasActiveSystemAdmin || org.role?.key === 'admin';

/**
 * プロジェクト設定リンク (歯車) の出し分け。`canShowProjectSettingsLink` と同じ判定。
 */
export const hasProjectSettingsAccessForNavi = (
  account: NaviAccountSource,
  org: NaviOrganizationSource,
  project: NaviProjectSource,
): boolean => account.hasActiveSystemAdmin || org.role?.key === 'admin' || project.group?.isProjectAdmin === true;

/**
 * 共通ナビのユーザー名直下に表示する役割ラベル。
 * プロジェクト管理者かどうかは group.isProjectAdmin のみで判定する（project role は参照しない）。
 */
export const toNaviRoleLabel = (params: { group?: { isProjectAdmin?: boolean } | null }): string => {
  if (Group.Is.ProjectAdmin({ isProjectAdmin: params.group?.isProjectAdmin ?? false })) {
    return '管理者';
  }
  return 'メンバー';
};

/** VoC の移行設定をグループポリシーから取得する。グループ未所属の場合は false を返す。 */
const getVocMigration = (params: { project: NaviProjectSource }): NonNullable<QantProductPermission['migration']> => {
  const { project } = params;
  if (!project.group) return { useKarteAsMain: false };
  return {
    useKarteAsMain: Group.Policy.UseKarteAsMain({ policy: project.group.policy, productKey: 'voc' }),
  };
};

/**
 * v1 `HasProduct管理権限` (knowledge lib-api authorization) parity の product 別管理権限判定:
 *   `hasActiveSystemAdmin || (PJメンバー && group.policy[productKey].isAdmin === true)`
 * ※ system admin の判定は auth 標準に合わせ active grant (`hasActiveSystemAdmin`) を使う。
 */
const isProductAdmin = (params: {
  account: Pick<QantNaviSource['Account'], 'hasActiveSystemAdmin'>;
  project: Pick<QantNaviSource['Organization'][number]['projects'][number], 'group'>;
  productKey: Product.Key;
}): boolean => {
  const { account, project, productKey } = params;
  if (account.hasActiveSystemAdmin) return true;
  if (!project.group) return false;
  return Group.Policy.IsAdmin({ policy: project.group.policy, productKey });
};

/**
 * v1 `AuthInfo.HasPiiAccessNow({ productKey })` parity の product 別 PII アクセス権限判定:
 *   `hasActivePiiAccess || group.policy[productKey].hasPiiAccess`
 * (アカウント単位の一時 PII グラント、または group policy の product 別 PII 権限)
 */
const isProductPii = (params: {
  account: Pick<QantNaviSource['Account'], 'hasActivePiiAccess'>;
  project: Pick<QantNaviSource['Organization'][number]['projects'][number], 'group'>;
  productKey: Product.Key;
}): boolean => {
  const { account, project, productKey } = params;
  if (account.hasActivePiiAccess) return true;
  if (!project.group) return false;
  return Group.Policy.HasPiiAccess({ policy: project.group.policy, productKey });
};

/**
 * メニュー出し分け用の facts を組み立てる (I/O を持たない純関数)。
 * `convertToQantNaviInputV2` (backend) と lib-front の bootstrap fallback から共用する。
 */
export const buildQantNaviFacts = (params: {
  account: Pick<QantNaviSource['Account'], 'isEmployee' | 'hasActiveSystemAdmin' | 'hasActivePiiAccess'>;
  project: Pick<QantNaviSource['Organization'][number]['projects'][number], 'group' | 'featureFlags'>;
}): QantNaviFacts => {
  const productAdmin: Partial<Record<QantProductKey, boolean>> = {};
  const productPii: Partial<Record<QantProductKey, boolean>> = {};
  for (const product of Product.List) {
    productAdmin[product.key as QantProductKey] = isProductAdmin({ ...params, productKey: product.key });
    productPii[product.key as QantProductKey] = isProductPii({ ...params, productKey: product.key });
  }
  const qantSmsEnabled = params.project.featureFlags?.qantSmsEnabled === true;
  return {
    isEmployee: params.account.isEmployee,
    productAdmin,
    productPii,
    ...(qantSmsEnabled ? { qantSmsEnabled: true } : {}),
  };
};
